import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bell, Loader2, Trash2, ExternalLink, RefreshCw } from "lucide-react";
import { apiService } from "../../services/api";

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
};

const NotificationBell = () => {
  const POLLING_INTERVAL_MS = 30_000;

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);
  const isNotificationSupported =
    typeof window !== "undefined" && "Notification" in window;
  const [browserPermission, setBrowserPermission] = useState(() => {
    if (!isNotificationSupported) {
      return "unsupported";
    }
    return window.Notification.permission;
  });
  const latestNotificationTimestampRef = useRef(null);
  const hasInitializedNotificationsRef = useRef(false);
  const isFetchingRef = useRef(false);

  const hasNotifications = notifications.length > 0;

  const requestBrowserPermission = useCallback(async () => {
    if (!isNotificationSupported) {
      return;
    }
    try {
      const result = await Notification.requestPermission();
      setBrowserPermission(result);
      if (result === "granted" && latestNotificationTimestampRef.current === null) {
        latestNotificationTimestampRef.current = Date.now();
        hasInitializedNotificationsRef.current = true;
      }
    } catch (err) {
      console.warn("Permission notification refusée :", err);
    }
  }, [isNotificationSupported]);

  const handleBrowserNotifications = useCallback(
    (items) => {
      if (!items || items.length === 0) {
        if (!hasInitializedNotificationsRef.current) {
          hasInitializedNotificationsRef.current = true;
        }
        return;
      }

      let maxTimestamp = latestNotificationTimestampRef.current ?? 0;
      const shouldNotify =
        isNotificationSupported && browserPermission === "granted";
      const notifyQueue = [];

      items.forEach((notification) => {
        const createdAt = new Date(notification.created_at ?? 0).getTime();
        if (Number.isNaN(createdAt)) {
          return;
        }
        if (createdAt > maxTimestamp) {
          maxTimestamp = createdAt;
        }
        if (
          shouldNotify &&
          hasInitializedNotificationsRef.current &&
          (latestNotificationTimestampRef.current === null ||
            createdAt > latestNotificationTimestampRef.current) &&
          notification.is_read === false
        ) {
          notifyQueue.push(notification);
        }
      });

      if (maxTimestamp > (latestNotificationTimestampRef.current ?? 0)) {
        latestNotificationTimestampRef.current = maxTimestamp;
      }

      if (!hasInitializedNotificationsRef.current) {
        hasInitializedNotificationsRef.current = true;
      }

      if (!shouldNotify || notifyQueue.length === 0) {
        return;
      }

      notifyQueue.forEach((notification) => {
        try {
          const body = notification.message || "";
          const title =
            notification.title || "Nouvelle notification Deepflow";
          const options = {
            body,
            tag: `deepflow-notification-${notification.id}`,
            data: {
              notificationId: notification.id,
            },
          };
          new Notification(title, options);
        } catch (err) {
          console.warn("Impossible d'afficher la notification navigateur:", err);
        }
      });
    },
    [browserPermission, isNotificationSupported]
  );

  const loadNotifications = useCallback(
    async (options = {}) => {
      const { skipLoadingIndicator = false } = options;
      if (isFetchingRef.current) {
        return;
      }
      isFetchingRef.current = true;
      try {
        if (!skipLoadingIndicator) {
          setLoading(true);
        }
        const data = await apiService.getNotifications();
        setNotifications(data.notifications ?? []);
        setUnreadCount(data.unread_count ?? 0);
        setError(null);
        handleBrowserNotifications(data.notifications ?? []);
      } catch (err) {
        console.error("Impossible de charger les notifications:", err);
        setError(err.message || "Chargement impossible");
      } finally {
        if (!skipLoadingIndicator) {
          setLoading(false);
        }
        isFetchingRef.current = false;
      }
    },
    [handleBrowserNotifications]
  );

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(
      () => loadNotifications({ skipLoadingIndicator: true }),
      POLLING_INTERVAL_MS
    );
    return () => clearInterval(interval);
  }, [loadNotifications]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        loadNotifications({ skipLoadingIndicator: true });
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loadNotifications]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const handleToggle = async () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (!open && isNotificationSupported && browserPermission === "default") {
      requestBrowserPermission();
    }
    if (!open && unreadCount > 0) {
      try {
        const data = await apiService.markAllNotificationsRead();
        setNotifications(data.notifications ?? []);
        setUnreadCount(data.unread_count ?? 0);
      } catch (err) {
        console.error("Erreur lors du passage en lu:", err);
      }
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await apiService.deleteNotification(notificationId);
      setNotifications((prev) =>
        prev.filter((notification) => notification.id !== notificationId)
      );
    } catch (err) {
      console.error("Suppression impossible:", err);
      setError(err.message || "Suppression impossible");
    }
  };

  const newestFirst = useMemo(
    () =>
      [...notifications].sort(
        (a, b) =>
          new Date(b.created_at ?? 0).getTime() -
          new Date(a.created_at ?? 0).getTime()
      ),
    [notifications]
  );

  const handleDeleteAll = async () => {
    if (notifications.length === 0) {
      return;
    }
    try {
      setClearing(true);
      await apiService.deleteAllNotifications();
      setNotifications([]);
      setUnreadCount(0);
      setError(null);
    } catch (err) {
      console.error("Suppression impossible:", err);
      setError(err.message || "Suppression impossible");
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={handleToggle}
        className="relative flex items-center justify-center h-10 w-10 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label="Notifications"
      >
        <Bell
          className={`h-5 w-5 ${
            unreadCount > 0 ? "text-blue-600" : "text-gray-600"
          }`}
        />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-semibold text-white bg-red-500 rounded-full">
            {unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white border border-gray-200 rounded-lg shadow-xl z-50">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  Notifications
                </p>
              <p className="text-xs text-gray-500">
                {unreadCount > 0
                  ? `${unreadCount} non lue${unreadCount > 1 ? "s" : ""}`
                  : "À jour"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDeleteAll}
                className="p-1.5 rounded-full text-gray-500 hover:text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-500"
                aria-label="Supprimer toutes les notifications"
                disabled={clearing || loading || notifications.length === 0}
              >
                {clearing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </button>
              <button
                onClick={loadNotifications}
                className="p-1.5 rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                aria-label="Rafraîchir les notifications"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </button>
            </div>
            </div>
            {isNotificationSupported && browserPermission === "default" && (
              <div className="px-4 py-2 bg-blue-50 text-xs text-blue-700 flex items-center justify-between gap-2">
                <span>Activer les notifications navigateur pour être alerté dès qu'un événement survient.</span>
                <button
                  type="button"
                  onClick={requestBrowserPermission}
                  className="px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Activer
                </button>
              </div>
            )}
            {isNotificationSupported && browserPermission === "denied" && (
              <div className="px-4 py-2 bg-yellow-50 text-xs text-yellow-700">
                Notifications navigateur bloquées. Autorisez-les dans les paramètres de votre navigateur pour recevoir les alertes.
              </div>
            )}

          <div className="max-h-96 overflow-y-auto">
            {error && (
              <div className="px-4 py-3 text-sm text-red-600 bg-red-50">
                {error}
              </div>
            )}

            {!hasNotifications && !loading && !error && (
              <div className="px-4 py-6 text-center text-sm text-gray-500">
                Aucune notification pour le moment.
              </div>
            )}

            {loading && (
              <div className="px-4 py-6 flex items-center justify-center text-sm text-gray-500">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Chargement...
              </div>
            )}

            {!loading &&
              newestFirst.map((notification) => (
                <div
                  key={notification.id}
                  className="px-4 py-3 border-b last:border-b-0 border-gray-100 hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {notification.title}
                      </p>
                      {notification.version && (
                        <p className="text-xs text-blue-600 font-medium mt-0.5">
                          Version {notification.version}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDelete(notification.id)}
                      className="p-1 text-gray-400 hover:text-red-500 rounded focus:outline-none focus:ring-2 focus:ring-red-400"
                      aria-label="Supprimer la notification"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mt-2 whitespace-pre-line">
                    {notification.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    {formatDateTime(notification.created_at)}
                  </p>
                  {notification.links && notification.links.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {notification.links.map((link) => (
                        <a
                          key={`${notification.id}-${link.url}`}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md"
                        >
                          <ExternalLink className="h-3 w-3" />
                          {link.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
